<template>
  <div class="loccal">
    <!-- GPS location -> weather -->
    <div class="mb-3">
      <label :for="`${idPrefix}-coords`" class="form-label">
        {{ $t('calendar.fields.coordsLabel') }} <span class="text-danger">*</span>
      </label>
      <input
        :id="`${idPrefix}-coords`"
        type="text"
        class="form-control"
        :class="{ 'is-invalid': coordsTouched && !coords }"
        :value="modelValue.coordinates"
        placeholder="50.85123, 2.88456"
        :disabled="disabled"
        @input="update({ coordinates: $event.target.value })"
        @blur="coordsTouched = true"
      />
      <p class="loccal-help">
        {{ $t('calendar.fields.coordsHelp') }}
      </p>
      <div v-if="coordsTouched && !coords" class="invalid-feedback d-block">
        {{ $t('calendar.fields.coordsInvalidBefore') }} <code>50.85123, 2.88456</code> {{ $t('calendar.fields.coordsInvalidAfter') }}
      </div>
      <a v-if="coords" :href="mapLink(coords)" target="_blank" rel="noopener" class="loccal-map">
        <i class="bi bi-geo-alt"></i> {{ $t('calendar.fields.checkOnMap', { coords: `${coords.lat.toFixed(5)}, ${coords.lon.toFixed(5)}` }) }}
      </a>
    </div>

    <!-- School-holiday region -->
    <div class="mb-3">
      <label :for="`${idPrefix}-region`" class="form-label">
        {{ $t('calendar.fields.schoolHolidays') }} <span v-if="regions.length" class="text-danger">*</span>
      </label>
      <p v-if="!countryCode" class="loccal-help">{{ $t('calendar.fields.pickCountryFirst') }}</p>
      <p v-else-if="regionsLoading" class="loccal-help">{{ $t('calendar.fields.loadingRegions') }}</p>
      <p v-else-if="regionsError" class="loccal-help text-danger">{{ regionsError }}</p>
      <template v-else-if="regions.length">
        <select
          :id="`${idPrefix}-region`"
          class="form-select"
          :value="modelValue.school_region || ''"
          :disabled="disabled"
          @change="pickRegion($event.target.value)"
        >
          <option value="" disabled>{{ $t('calendar.fields.regionPlaceholder') }}</option>
          <option v-for="r in regions" :key="r.code" :value="r.code">{{ r.name }}</option>
        </select>
        <p class="loccal-help">{{ $t('calendar.fields.publicHolidaysNote') }}</p>
      </template>
      <p v-else class="loccal-help">{{ $t('calendar.fields.nationalCalendar') }}</p>
    </div>

    <!-- Sector calendars (e.g. bouwverlof) -->
    <div v-if="countrySectors.length" class="mb-3">
      <span class="form-label d-block">{{ $t('calendar.fields.sectorHolidays') }}</span>
      <p class="loccal-help">{{ $t('calendar.fields.sectorHelp') }}</p>
      <div v-for="cal in countrySectors" :key="cal.key" class="form-check">
        <input
          :id="`${idPrefix}-sector-${cal.key}`"
          type="checkbox"
          class="form-check-input"
          :checked="(modelValue.sector || []).includes(cal.key)"
          :disabled="disabled"
          @change="toggleSector(cal.key, $event.target.checked)"
        />
        <label class="form-check-label" :for="`${idPrefix}-sector-${cal.key}`">
          {{ splitName(cal.entries[0]?.Name, cal.key).title }}
        </label>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch, onMounted } from 'vue'

import { useLocationCalendarStore } from '@/stores/LocationCalendarStore'
import { parseCoordinates, mapLink } from '@/location/coordinates'
import { splitName } from '@/calendars/sectorCalendars'

// v-model: { coordinates: '50.85, 2.88', school_region, school_region_type, sector: [] }
// Emits `valid` whenever completeness changes: coordinates parse, and a school region
// is picked when the country has regions.
export default {
    name: 'LocationCalendarFields',

    props: {
        modelValue: { type: Object, required: true },
        countryCode: { type: String, default: '' },
        disabled: { type: Boolean, default: false },
        idPrefix: { type: String, default: 'loccal' },
    },

    emits: ['update:modelValue', 'valid'],

    setup(props, { emit }) {
        const store = useLocationCalendarStore()
        const coordsTouched = ref(false)
        const regions = ref([])
        const regionsLoading = ref(false)
        const regionsError = ref('')

        const coords = computed(() => parseCoordinates(props.modelValue.coordinates))
        const countrySectors = computed(() =>
            store.sectorCalendars.filter(c => c.country === props.countryCode))

        const update = (patch) => emit('update:modelValue', { ...props.modelValue, ...patch })

        function pickRegion(code) {
            const r = regions.value.find(x => x.code === code)
            update({ school_region: r ? r.code : null, school_region_type: r ? r.type : null })
        }

        function toggleSector(key, on) {
            const set = new Set(props.modelValue.sector || [])
            on ? set.add(key) : set.delete(key)
            update({ sector: [...set] })
        }

        async function loadRegions(code) {
            regions.value = []
            regionsError.value = ''
            if (!code) return
            regionsLoading.value = true
            try {
                regions.value = await store.fetchRegions(code)
                // A region from another country (country changed) is no longer valid.
                if (props.modelValue.school_region && !regions.value.some(r => r.code === props.modelValue.school_region)) {
                    update({ school_region: null, school_region_type: null })
                }
            } catch (err) {
                regionsError.value = err.message
            } finally {
                regionsLoading.value = false
            }
        }

        const valid = computed(() =>
            !!coords.value && !regionsLoading.value && !regionsError.value && !!props.countryCode
            && (regions.value.length === 0 || !!props.modelValue.school_region))
        watch(valid, v => emit('valid', v), { immediate: true })

        watch(() => props.countryCode, loadRegions)
        onMounted(() => {
            loadRegions(props.countryCode)
            store.fetchSectorCalendars()
        })

        return { coords, coordsTouched, regions, regionsLoading, regionsError, countrySectors,
                 update, pickRegion, toggleSector, mapLink, splitName }
    },
}
</script>

<style scoped>
.loccal-help { font-size: .85rem; color: #6b7a86; margin: .25rem 0 0; }
.loccal-map { display: inline-flex; align-items: center; gap: 6px; margin-top: .35rem; font-size: .9rem; color: #4F7EB3; }
</style>
